/**
 * DriftController — keeps a follower's <video>.currentTime aligned to the
 * projected shared target during playback. Correction ladder:
 *   |err| < SOFT              -> hold (dead-band, rate 1.0)   no hunting
 *   SOFT <= |err| < HARD      -> proportional asymmetric rate nudge (0.9..1.1)
 *   |err| >= HARD (sustained) -> hard seek to target (+ latency lead)
 *
 * err = currentTime - target.  err > 0 => AHEAD (slow down); err < 0 => BEHIND
 * (speed up). Speeding up the behind client is preferred over slowing the ahead
 * one because humans notice slow-motion/pitch-down more; the clamps are
 * asymmetric to reflect that.
 *
 * The controller is a pure state machine (only holds the hard-seek hysteresis
 * counter and a seek-settle deadline); all timing is passed in.
 */
import { DRIFT } from "../shared/protocol.ts";

export type Correction =
  | { mode: "hold"; rate: 1 }
  | { mode: "nudge"; rate: number }
  | { mode: "seek"; rate: 1; seekToS: number };

export interface DriftInput {
  /** currentTime - target, in seconds. */
  errS: number;
  /** one-way latency estimate (ms), our precision floor. */
  rttHalfMs: number;
  /** target position we would seek to (seconds), already latency-projected. */
  targetS: number;
  /** true while logically paused — no correction. */
  paused: boolean;
  /** monotonic ms, for the post-seek settle window. */
  nowMs: number;
}

export function softBandS(rttHalfMs: number): number {
  const band = Math.max(DRIFT.SOFT_FLOOR_S, rttHalfMs / 1000 + DRIFT.SOFT_MARGIN_S);
  // Never let the dead-band reach the hard-seek threshold: on a very high-latency
  // link an unbounded band would swallow the nudge range AND the hard-seek path,
  // leaving a gross desync uncorrected forever. Keep it strictly below HARD_S.
  return Math.min(DRIFT.HARD_S - 0.1, band);
}

export class DriftController {
  private overHardCount = 0;
  private settleUntilMs = 0;

  /** Call when we intentionally seek (user or resume) to avoid measuring transients. */
  suppressUntil(nowMs: number, settleMs: number): void {
    this.settleUntilMs = Math.max(this.settleUntilMs, nowMs + settleMs);
  }

  reset(): void {
    this.overHardCount = 0;
    this.settleUntilMs = 0;
  }

  evaluate(input: DriftInput): Correction {
    if (input.paused || input.nowMs < this.settleUntilMs) {
      this.overHardCount = 0;
      return { mode: "hold", rate: 1 };
    }

    const soft = softBandS(input.rttHalfMs);
    const abs = Math.abs(input.errS);

    if (abs < soft) {
      this.overHardCount = 0;
      return { mode: "hold", rate: 1 };
    }

    if (abs < DRIFT.HARD_S) {
      this.overHardCount = 0;
      // Proportional: rate = clamp(1 - err/TAU). err<0 (behind) -> rate>1.
      const raw = 1 - input.errS / DRIFT.TAU_S;
      const rate = Math.min(DRIFT.RATE_MAX, Math.max(DRIFT.RATE_MIN, raw));
      return { mode: "nudge", rate };
    }

    // Gross desync: require HARD_CONSECUTIVE samples so one GC/decoder hiccup
    // does not cause a jarring jump.
    this.overHardCount += 1;
    if (this.overHardCount >= DRIFT.HARD_CONSECUTIVE) {
      this.overHardCount = 0;
      this.suppressUntil(input.nowMs, 0); // caller may extend with SEEK_SETTLE
      return { mode: "seek", rate: 1, seekToS: input.targetS };
    }
    // Not yet confirmed — hold at normal rate this tick.
    return { mode: "hold", rate: 1 };
  }
}
